import React from 'react';
import { isDesktopOnlyPanel, isWebApp } from '../platform/target';
import { useDocsSidebarOverlay } from './document-editor/useDocsSidebarOverlay';

/**
 * Layout shell for the primary content area. Arranges the active view but does
 * not own its wiring — Teams/Admin/sidebar views are passed pre-rendered, and
 * the editor + AI pane is passed as `children` (rendered only for the
 * collections/history view). Keeps the sidebar resizer placement here.
 */
export default function AppContent({
  activeTab,
  sidebarWidth,
  isSidebarResizing,
  onSidebarResizeStart,
  teamsView,
  sidebarView,
  keepAlive,
  children,
}) {
  // `keepAlive` (the Terminal) must survive EVERY tab switch: unmounting it
  // disposes each xterm and kills the shells/SSH sessions behind it. It sits
  // at the same position (second child of the outer fragment) in both
  // branches, so React keeps the instance when Teams swaps the rest out.
  // It also sits BEFORE `children` so the row reads [sidebar][terminal][AI
  // panel] — after `children` the AI panel would open left of the terminal.
  const isTeams = activeTab === 'teams';
  return (
    <>
      {isTeams ? (
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden h-full app-content-area">
          {teamsView}
        </div>
      ) : (
        <SidebarColumn
          activeTab={activeTab}
          sidebarWidth={sidebarWidth}
          isSidebarResizing={isSidebarResizing}
          onSidebarResizeStart={onSidebarResizeStart}
          sidebarView={sidebarView}
        />
      )}
      {keepAlive}
      {/* Scrollable Main Content Area + AI Panel */}
      {isTeams ? null : children}
    </>
  );
}

function SidebarColumn({ activeTab, sidebarWidth, isSidebarResizing, onSidebarResizeStart, sidebarView }) {
  const docs = useDocsSidebarOverlay(activeTab);

  // Task Manager, Terminal and Calendar use the normal layout below but
  // contribute no sidebar — their column is collapsed to zero width instead
  // of reserving the shared sidebarWidth (Terminal is full-width by design;
  // Calendar went full-bleed in fix round 3: a MiniMonth sidebar next to a
  // month grid read as a double calendar).
  const hasSidebar =
    activeTab !== 'task-manager.panel' &&
    activeTab !== 'terminal.panel' &&
    activeTab !== 'calendar.panel' &&
    activeTab !== 'graph.panel' &&
    activeTab !== 'projects.panel' &&
    activeTab !== 'mail-catcher.panel' &&
    activeTab !== 'git' &&
    // web app: a desktop-only page shows a full-width notice, no sidebar column
    !(isWebApp() && isDesktopOnlyPanel(activeTab));

  const resizer = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      className={`resizer resizer--horizontal resizer--sidebar self-stretch${isSidebarResizing ? ' dragging' : ''}`}
      onMouseDown={onSidebarResizeStart}
    />
  );

  return (
    <>
      {/* Fixed Sidebar Content */}
      {/* The resizer is also the visible separator between the sidebar and the
          content, so it belongs on whichever edge those two actually meet.

          It used to move to the sidebar's LEFT under sidebarPosition 'right',
          on the assumption that the sidebar moved to the right with it. It does
          not. `html.sidebar-right` reverses only `.app-main-layout`, whose
          direct children are the icon rail and the content wrapper — that swaps
          the RAIL to the far right. The sidebar column lives INSIDE the content
          wrapper and is never reversed, so it stays on the left in both modes
          (which the owner confirmed is the wanted behaviour).

          The layout is therefore [sidebar][content][rail], and the separator
          sits between sidebar and content — the sidebar's right edge — in BOTH
          modes. Flipping it just parked it against the window edge with nothing
          on the other side of it. */}
      {/* Documents below 900 px (card 2.6): the column collapses and the
          sidebar opens as an overlay from the document toolbar. */}
      <div className={`flex flex-shrink-0 h-full app-sidebar-column${docs.overlay ? ' app-sidebar-column--overlay' : ''}`}>
        <div
          ref={docs.wrapRef}
          className={`flex-shrink-0 h-full overflow-hidden app-sidebar-wrap${docs.open ? ' is-open' : ''}`}
          style={{ width: hasSidebar ? sidebarWidth : 0 }}
        >
          {hasSidebar && sidebarView}
        </div>
        {hasSidebar && !docs.overlay && resizer}
      </div>
    </>
  );
}
